import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell
} from '@/components/ui/table';
import {
  Package, Plus, Search, QrCode, Trash2, Edit, Tag, Wrench,
  CheckCircle2, AlertCircle, RefreshCcw, Camera, ArrowDownToLine,
  LayoutDashboard, ListChecks, UserCheck, Settings, Eye, ExternalLink
} from 'lucide-react';

const STATUS_COLORS = {
  available: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  assigned: 'bg-blue-500/10 text-blue-700 border-blue-500/30',
  in_service: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  retired: 'bg-slate-500/10 text-slate-700 border-slate-500/30',
  lost: 'bg-red-500/10 text-red-700 border-red-500/30',
};

const fmtCur = (n, c = 'AED') => `${c} ${(n || 0).toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function AssetManagementPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Modals
  const [catModal, setCatModal] = useState({ open: false, data: null });
  const [assetModal, setAssetModal] = useState({ open: false, data: null });
  const [assignModal, setAssignModal] = useState({ open: false, asset: null, employee_id: '', notes: '', condition_notes: '', photos: [] });
  const [returnModal, setReturnModal] = useState({ open: false, asset: null, condition: 'good', return_notes: '', damage_summary: '', photos: [] });
  const [serviceModal, setServiceModal] = useState({ open: false, asset: null, type: 'breakdown', cause: 'normal_use', charge_to: 'company', description: '', estimated_cost: '' });
  const [detailModal, setDetailModal] = useState({ open: false, asset: null });

  // ─── Loaders ───────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, cats, emps] = await Promise.all([
        apiClient.get('/assets/dashboard'),
        apiClient.get('/assets/categories'),
        // Pull both active AND probation employees — newly-added sales
        // executives are on probation by default and were being filtered
        // out by the `status=active` query param.
        apiClient.get('/hr/employees?limit=500').catch(() => ({ data: [] })),
      ]);
      setDashboard(dash.data);
      setCategories(cats.data?.categories || []);
      const empList = Array.isArray(emps.data) ? emps.data
        : Array.isArray(emps.data?.employees) ? emps.data.employees : [];
      // Include both `active` and `probation` employees; exclude only
      // terminated/resigned/absconding so they don't pollute the dropdown.
      const EXCLUDED = new Set(['terminated', 'resigned', 'absconding', 'inactive']);
      setEmployees(empList.filter(e => !EXCLUDED.has((e.employment_status || 'active').toLowerCase())));
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAssets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (categoryFilter !== 'all') params.set('category_id', categoryFilter);
      const r = await apiClient.get(`/assets?${params.toString()}`);
      setAssets(Array.isArray(r.data) ? r.data : []);
    } catch {
      setAssets([]);
    }
  }, [search, statusFilter, categoryFilter]);

  const fetchServices = useCallback(async () => {
    try {
      const r = await apiClient.get('/assets/service/list');
      setServices(Array.isArray(r.data) ? r.data : []);
    } catch {
      setServices([]);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (tab === 'inventory') fetchAssets(); }, [tab, fetchAssets]);
  useEffect(() => { if (tab === 'service') fetchServices(); }, [tab, fetchServices]);

  // Open detail dialog if URL has ?asset=
  useEffect(() => {
    const id = searchParams.get('asset');
    if (id && !detailModal.open) openDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  async function openDetail(assetId) {
    try {
      const r = await apiClient.get(`/assets/${assetId}`);
      setDetailModal({ open: true, asset: r.data });
    } catch {
      toast.error('Asset not found');
    }
  }

  // ─── Category CRUD ─────────────────────────────────────────────────
  async function saveCategory() {
    const d = catModal.data;
    if (!d?.name?.trim()) return toast.error('Name required');
    try {
      if (d.id) await apiClient.put(`/assets/categories/${d.id}`, d);
      else await apiClient.post('/assets/categories', d);
      toast.success('Category saved');
      setCatModal({ open: false, data: null });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save category');
    }
  }
  async function deleteCategory(id) {
    if (!window.confirm('Delete this category?')) return;
    try {
      await apiClient.delete(`/assets/categories/${id}`);
      toast.success('Category deleted');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  }

  // ─── Asset CRUD ────────────────────────────────────────────────────
  async function saveAsset() {
    const d = assetModal.data || {};
    if (!d.category_id) return toast.error('Category required');
    if (d.purchase_value !== undefined) d.purchase_value = parseFloat(d.purchase_value) || 0;
    if (d.depreciation_pct !== undefined) d.depreciation_pct = parseFloat(d.depreciation_pct) || 0;
    try {
      if (d.id) await apiClient.put(`/assets/${d.id}`, d);
      else await apiClient.post('/assets', d);
      toast.success('Asset saved');
      setAssetModal({ open: false, data: null });
      fetchAll();
      fetchAssets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save asset');
    }
  }
  async function deleteAsset(id) {
    if (!window.confirm('Delete this asset? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/assets/${id}`);
      toast.success('Asset deleted');
      fetchAll(); fetchAssets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  }

  // ─── Assignment ────────────────────────────────────────────────────
  async function submitAssign() {
    const m = assignModal;
    if (!m.employee_id) return toast.error('Pick an employee');
    const fd = new FormData();
    fd.append('employee_id', m.employee_id);
    if (m.notes) fd.append('notes', m.notes);
    if (m.condition_notes) fd.append('condition_notes', m.condition_notes);
    (m.photos || []).forEach(p => fd.append('photos', p));
    try {
      await apiClient.post(`/assets/${m.asset.id}/assign`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Asset assigned');
      setAssignModal({ open: false, asset: null, employee_id: '', notes: '', condition_notes: '', photos: [] });
      fetchAll(); fetchAssets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to assign');
    }
  }

  // ─── Return ────────────────────────────────────────────────────────
  async function submitReturn() {
    const m = returnModal;
    const fd = new FormData();
    fd.append('condition', m.condition || 'good');
    if (m.return_notes) fd.append('return_notes', m.return_notes);
    if (m.damage_summary) fd.append('damage_summary', m.damage_summary);
    (m.photos || []).forEach(p => fd.append('photos', p));
    try {
      await apiClient.post(`/assets/${m.asset.id}/return`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Asset returned');
      setReturnModal({ open: false, asset: null, condition: 'good', return_notes: '', damage_summary: '', photos: [] });
      fetchAll(); fetchAssets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to return');
    }
  }

  // ─── Service ───────────────────────────────────────────────────────
  async function submitService() {
    const m = serviceModal;
    if (!m.description?.trim()) return toast.error('Description required');
    try {
      await apiClient.post(`/assets/${m.asset.id}/service`, {
        type: m.type, cause: m.cause, charge_to: m.charge_to,
        description: m.description,
        estimated_cost: parseFloat(m.estimated_cost) || 0,
      });
      toast.success(m.charge_to === 'employee' ? 'Service opened — HR notified about personal damage' : 'Service ticket opened');
      setServiceModal({ open: false, asset: null, type: 'breakdown', cause: 'normal_use', charge_to: 'company', description: '', estimated_cost: '' });
      fetchAll(); fetchAssets(); fetchServices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  }
  async function closeServiceTicket(ticketId, status, condition_after = 'good') {
    try {
      await apiClient.put(`/assets/service/${ticketId}`, { status, condition_after });
      toast.success(`Ticket ${status}`);
      fetchAll(); fetchServices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  }

  async function recomputeDep() {
    try {
      const r = await apiClient.post('/assets/depreciation/recompute');
      toast.success(`Depreciation recomputed for ${r.data?.updated || 0} assets`);
      fetchAll();
    } catch {
      toast.error('Recompute failed');
    }
  }

  // ─── QR helpers ────────────────────────────────────────────────────
  // <img> tags cannot send Authorization headers — the QR endpoint requires
  // a Bearer token so we fetch the PNG as a blob and turn it into an
  // object URL. Without this, the IT admin sees a broken-image icon when
  // they open the QR dialog (the "scanner not working" complaint).
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  useEffect(() => {
    let revoke = null;
    if (detailModal.open && detailModal.asset?.id) {
      setQrLoading(true);
      setQrBlobUrl(null);
      apiClient.get(`/assets/${detailModal.asset.id}/qr.png`, { responseType: 'blob' })
        .then(res => {
          const url = URL.createObjectURL(res.data);
          revoke = url;
          setQrBlobUrl(url);
        })
        .catch(() => toast.error('Failed to load QR code'))
        .finally(() => setQrLoading(false));
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [detailModal.open, detailModal.asset?.id]);

  function openQr(asset) {
    setDetailModal({ open: true, asset });
    // QR will be displayed inside the detail dialog
  }

  // ────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="asset-management-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
            <Package className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">IT & Asset Management</h1>
            <p className="text-muted-foreground text-sm">Track every device — value, location, condition.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={recomputeDep} data-testid="recompute-dep-btn">
            <RefreshCcw className="h-4 w-4 mr-1.5" /> Recompute depreciation
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory"><ListChecks className="h-4 w-4 mr-1.5" /> Inventory</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments"><UserCheck className="h-4 w-4 mr-1.5" /> Assignments</TabsTrigger>
          <TabsTrigger value="service" data-testid="tab-service"><Wrench className="h-4 w-4 mr-1.5" /> Service Tracker</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories"><Settings className="h-4 w-4 mr-1.5" /> Categories</TabsTrigger>
        </TabsList>

        {/* ═══ DASHBOARD ═══ */}
        <TabsContent value="dashboard" className="space-y-4">
          {!dashboard ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : (
            <>
              {/* KPI row */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard title="Total Assets" value={dashboard.total_assets} icon={Package} />
                <StatCard title="Available" value={dashboard.by_status?.available || 0} accent="emerald" />
                <StatCard title="Assigned" value={dashboard.by_status?.assigned || 0} accent="blue" />
                <StatCard title="In Service" value={dashboard.by_status?.in_service || 0} accent="amber" />
                <StatCard title="Open Tickets" value={dashboard.open_service_count || 0} accent="red" icon={Wrench} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <StatCard title="Purchase Value (Total)" value={fmtCur(dashboard.purchase_total)} accent="slate" />
                <StatCard title="Current Book Value" value={fmtCur(dashboard.current_value_total)} accent="emerald" />
                <StatCard title="Depreciation To Date" value={fmtCur(dashboard.depreciation_to_date)} accent="amber" />
              </div>

              {/* By category */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">By Category</CardTitle></CardHeader>
                <CardContent>
                  {(dashboard.by_category || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">No categories yet. Create one in the Categories tab.</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {dashboard.by_category.map(c => (
                        <button
                          key={c.category_id}
                          onClick={() => { setCategoryFilter(c.category_id); setTab('inventory'); }}
                          className="text-left p-4 rounded-xl border bg-card hover:border-primary/40 hover:shadow transition"
                          data-testid={`cat-card-${c.category_id}`}>
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-muted-foreground" />
                            <span className="font-semibold">{c.category_name}</span>
                          </div>
                          <p className="text-2xl font-bold mt-2">{c.count}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {c.available} avail • {c.assigned} assigned • {c.in_service} service
                          </p>
                          <p className="text-xs font-mono mt-2 text-emerald-600">{fmtCur(c.current_total)}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top assignees */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-base">Top Asset Holders</CardTitle></CardHeader>
                  <CardContent>
                    {(dashboard.top_assignees || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground py-6 text-center">No assignments yet</p>
                    ) : (
                      <div className="space-y-2">
                        {dashboard.top_assignees.map(a => (
                          <div key={a.employee_id} className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50">
                            <div className="text-sm">
                              <p className="font-medium">{a.employee_name}</p>
                              <p className="text-xs text-muted-foreground">{a.count} asset{a.count > 1 ? 's' : ''}</p>
                            </div>
                            <span className="font-mono text-sm text-emerald-700">{fmtCur(a.total_value)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-base">Recent Service Activity</CardTitle></CardHeader>
                  <CardContent>
                    {(dashboard.recent_service || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground py-6 text-center">No service tickets</p>
                    ) : (
                      <div className="space-y-2">
                        {dashboard.recent_service.map(s => (
                          <div key={s.id} className="flex items-center justify-between p-2 rounded-md border">
                            <div className="text-sm min-w-0 flex-1">
                              <p className="font-medium truncate">{s.asset_code} — {s.type}</p>
                              <p className="text-xs text-muted-foreground truncate">{s.description}</p>
                            </div>
                            <Badge variant="outline" className={s.is_personal_damage ? 'bg-red-500/10 text-red-700' : ''}>
                              {s.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* ═══ INVENTORY ═══ */}
        <TabsContent value="inventory" className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search code, UID, IP, serial, brand..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="asset-search" />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[170px]" data-testid="cat-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_service">In Service</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setAssetModal({ open: true, data: { category_id: categories[0]?.id || '', depreciation_method: 'straight_line', depreciation_pct: 20 } })} data-testid="add-asset-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add Asset
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>UID / Serial</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Brand / Model</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned to</TableHead>
                    <TableHead className="text-right">Purchase</TableHead>
                    <TableHead className="text-right">Current</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assets.map(a => (
                    <TableRow key={a.id} data-testid={`asset-row-${a.asset_code}`}>
                      <TableCell className="font-mono text-xs">{a.asset_code}</TableCell>
                      <TableCell className="text-xs">{a.uid || '—'}<br /><span className="text-muted-foreground">{a.serial_number || ''}</span></TableCell>
                      <TableCell className="text-sm">{a.category_name}</TableCell>
                      <TableCell className="text-sm">{a.brand} {a.model}</TableCell>
                      <TableCell><Badge variant="outline" className={STATUS_COLORS[a.status] || ''}>{a.status}</Badge></TableCell>
                      <TableCell className="text-sm">{a.assigned_to_name || '—'}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmtCur(a.purchase_value, a.currency)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-emerald-700">{fmtCur(a.current_value, a.currency)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openDetail(a.id)} title="Details" data-testid={`view-${a.asset_code}`}><Eye className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => openQr(a)} title="QR Tag" data-testid={`qr-${a.asset_code}`}><QrCode className="h-4 w-4" /></Button>
                          {a.status === 'available' && (
                            <Button size="icon" variant="ghost" onClick={() => { fetchAll(); setAssignModal({ open: true, asset: a, employee_id: '', notes: '', condition_notes: '', photos: [] }); }} title="Assign" data-testid={`assign-${a.asset_code}`}>
                              <UserCheck className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                          {a.status === 'assigned' && (
                            <Button size="icon" variant="ghost" onClick={() => setReturnModal({ open: true, asset: a, condition: 'good', return_notes: '', damage_summary: '', photos: [] })} title="Return" data-testid={`return-${a.asset_code}`}>
                              <ArrowDownToLine className="h-4 w-4 text-emerald-600" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" onClick={() => setServiceModal({ open: true, asset: a, type: 'breakdown', cause: 'normal_use', charge_to: 'company', description: '', estimated_cost: '' })} title="Open service" data-testid={`service-${a.asset_code}`}>
                            <Wrench className="h-4 w-4 text-amber-600" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => setAssetModal({ open: true, data: a })} title="Edit"><Edit className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => deleteAsset(a.id)} title="Delete"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {assets.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No assets match the filter.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ ASSIGNMENTS ═══ */}
        <TabsContent value="assignments" className="space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Currently Assigned</CardTitle></CardHeader>
            <CardContent>
              {assets.filter(a => a.status === 'assigned').length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No assets currently assigned.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Asset</TableHead>
                      <TableHead>Holder</TableHead>
                      <TableHead className="text-right">Current Value</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assets.filter(a => a.status === 'assigned').map(a => (
                      <TableRow key={a.id}>
                        <TableCell className="font-mono text-xs">{a.asset_code}</TableCell>
                        <TableCell className="text-sm">{a.brand} {a.model}</TableCell>
                        <TableCell className="text-sm">{a.assigned_to_name}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{fmtCur(a.current_value, a.currency)}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => setReturnModal({ open: true, asset: a, condition: 'good', return_notes: '', damage_summary: '', photos: [] })}>
                            <ArrowDownToLine className="h-4 w-4 mr-1.5" /> Return
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ SERVICE TRACKER ═══ */}
        <TabsContent value="service" className="space-y-4">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Service Tickets</CardTitle>
              <Button variant="outline" size="sm" onClick={fetchServices}><RefreshCcw className="h-4 w-4 mr-1.5" /> Refresh</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Cause</TableHead>
                    <TableHead>Charge to</TableHead>
                    <TableHead>Holder</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {services.map(s => (
                    <TableRow key={s.id} data-testid={`service-row-${s.id}`}>
                      <TableCell className="font-mono text-xs">{s.asset_code}</TableCell>
                      <TableCell className="text-xs">{s.type}</TableCell>
                      <TableCell className="text-xs">{s.cause}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={s.charge_to === 'employee' ? 'bg-red-500/10 text-red-700' : 'bg-blue-500/10 text-blue-700'}>
                          {s.charge_to}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{s.asset_holder_name || '—'}</TableCell>
                      <TableCell className="text-xs max-w-[260px] truncate">{s.description}</TableCell>
                      <TableCell><Badge variant="outline">{s.status}</Badge></TableCell>
                      <TableCell>
                        {s.status !== 'completed' && s.status !== 'cancelled' && (
                          <div className="flex gap-1">
                            {s.status === 'open' && (
                              <Button size="sm" variant="outline" onClick={() => closeServiceTicket(s.id, 'in_progress')}>Start</Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => closeServiceTicket(s.id, 'completed')} data-testid={`close-${s.id}`}>
                              <CheckCircle2 className="h-4 w-4 mr-1" /> Close
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {services.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No service tickets.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ CATEGORIES ═══ */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between">
            <p className="text-sm text-muted-foreground">Categories drive default depreciation rules and the auto-generated asset codes.</p>
            <Button onClick={() => setCatModal({ open: true, data: { default_depreciation_pct: 20, default_useful_life_years: 5, default_depreciation_method: 'straight_line', requires_serial: true } })} data-testid="add-cat-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add Category
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {categories.map(c => (
              <Card key={c.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">{c.name}</CardTitle>
                      <CardDescription className="text-xs">{c.asset_count} asset{c.asset_count !== 1 ? 's' : ''} • prefix <code>{c.code_prefix}</code></CardDescription>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setCatModal({ open: true, data: c })}><Edit className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteCategory(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="text-xs space-y-1 pt-1">
                  <p>Depreciation: <span className="font-medium">{c.default_depreciation_pct}% / yr</span> ({c.default_depreciation_method === 'declining_balance' ? 'Declining' : 'Straight-line'})</p>
                  <p>Useful life: {c.default_useful_life_years} years</p>
                  <p>Requires: {[c.requires_serial && 'Serial', c.requires_ip && 'IP', c.requires_imei && 'IMEI'].filter(Boolean).join(', ') || 'Nothing'}</p>
                  <p>Photos on assign: {c.requires_photos_on_assign ? `min ${c.min_photos_required}` : 'optional'}</p>
                </CardContent>
              </Card>
            ))}
            {categories.length === 0 && <p className="col-span-full text-center text-muted-foreground py-8">No categories yet.</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* ═══ CATEGORY MODAL ═══ */}
      <Dialog open={catModal.open} onOpenChange={o => !o && setCatModal({ open: false, data: null })}>
        <DialogContent className="max-w-md" data-testid="cat-modal">
          <DialogHeader>
            <DialogTitle>{catModal.data?.id ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={catModal.data?.name || ''} onChange={e => setCatModal({ ...catModal, data: { ...catModal.data, name: e.target.value } })} data-testid="cat-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code Prefix</Label><Input maxLength={6} value={catModal.data?.code_prefix || ''} onChange={e => setCatModal({ ...catModal, data: { ...catModal.data, code_prefix: e.target.value.toUpperCase() } })} placeholder="LAP" /></div>
              <div><Label>Useful Life (yrs)</Label><Input type="number" value={catModal.data?.default_useful_life_years || 5} onChange={e => setCatModal({ ...catModal, data: { ...catModal.data, default_useful_life_years: parseInt(e.target.value) || 0 } })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Depreciation %/yr</Label><Input type="number" step="0.1" value={catModal.data?.default_depreciation_pct || 0} onChange={e => setCatModal({ ...catModal, data: { ...catModal.data, default_depreciation_pct: parseFloat(e.target.value) || 0 } })} /></div>
              <div>
                <Label>Method</Label>
                <Select value={catModal.data?.default_depreciation_method || 'straight_line'} onValueChange={v => setCatModal({ ...catModal, data: { ...catModal.data, default_depreciation_method: v } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="straight_line">Straight-line</SelectItem>
                    <SelectItem value="declining_balance">Declining balance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm pt-1">
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!catModal.data?.requires_serial} onChange={e => setCatModal({ ...catModal, data: { ...catModal.data, requires_serial: e.target.checked } })} /> Serial</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!catModal.data?.requires_ip} onChange={e => setCatModal({ ...catModal, data: { ...catModal.data, requires_ip: e.target.checked } })} /> IP</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!catModal.data?.requires_imei} onChange={e => setCatModal({ ...catModal, data: { ...catModal.data, requires_imei: e.target.checked } })} /> IMEI</label>
            </div>
            <div>
              <Label className="text-xs">Photos required on assignment?</Label>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={!!catModal.data?.requires_photos_on_assign} onChange={e => setCatModal({ ...catModal, data: { ...catModal.data, requires_photos_on_assign: e.target.checked } })} />
                <span className="text-xs">Min</span>
                <Input type="number" className="w-16 h-8" disabled={!catModal.data?.requires_photos_on_assign} value={catModal.data?.min_photos_required || 0} onChange={e => setCatModal({ ...catModal, data: { ...catModal.data, min_photos_required: parseInt(e.target.value) || 0 } })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatModal({ open: false, data: null })}>Cancel</Button>
            <Button onClick={saveCategory} data-testid="save-cat">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ ASSET MODAL ═══ */}
      <Dialog open={assetModal.open} onOpenChange={o => !o && setAssetModal({ open: false, data: null })}>
        <DialogContent className="max-w-2xl" data-testid="asset-modal">
          <DialogHeader>
            <DialogTitle>{assetModal.data?.id ? 'Edit Asset' : 'New Asset'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category *</Label>
                <Select value={assetModal.data?.category_id || ''} onValueChange={v => {
                  const cat = categories.find(c => c.id === v);
                  setAssetModal({ ...assetModal, data: { ...assetModal.data, category_id: v,
                    depreciation_pct: cat?.default_depreciation_pct ?? assetModal.data?.depreciation_pct ?? 20,
                    depreciation_method: cat?.default_depreciation_method || assetModal.data?.depreciation_method || 'straight_line',
                    useful_life_years: cat?.default_useful_life_years ?? assetModal.data?.useful_life_years ?? 5,
                  } });
                }}>
                  <SelectTrigger data-testid="asset-cat-select"><SelectValue placeholder="Pick category" /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>UID / Tag Number</Label><Input value={assetModal.data?.uid || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, uid: e.target.value } })} placeholder="e.g. CLT-LAP-007" data-testid="asset-uid" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Brand</Label><Input value={assetModal.data?.brand || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, brand: e.target.value } })} /></div>
              <div><Label>Model</Label><Input value={assetModal.data?.model || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, model: e.target.value } })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Serial Number</Label><Input value={assetModal.data?.serial_number || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, serial_number: e.target.value } })} /></div>
              <div><Label>IP Address</Label><Input value={assetModal.data?.ip_address || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, ip_address: e.target.value } })} placeholder="192.168.1.42" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>MAC Address</Label><Input value={assetModal.data?.mac_address || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, mac_address: e.target.value } })} /></div>
              <div><Label>IMEI</Label><Input value={assetModal.data?.imei || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, imei: e.target.value } })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Purchase Date</Label><Input type="date" value={assetModal.data?.purchase_date || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, purchase_date: e.target.value } })} /></div>
              <div><Label>Purchase Value</Label><Input type="number" step="0.01" value={assetModal.data?.purchase_value ?? ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, purchase_value: e.target.value } })} data-testid="asset-purchase-value" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Currency</Label><Input value={assetModal.data?.currency || 'AED'} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, currency: e.target.value } })} /></div>
              <div><Label>Depreciation %/yr</Label><Input type="number" step="0.1" value={assetModal.data?.depreciation_pct ?? 20} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, depreciation_pct: e.target.value } })} /></div>
              <div>
                <Label>Method</Label>
                <Select value={assetModal.data?.depreciation_method || 'straight_line'} onValueChange={v => setAssetModal({ ...assetModal, data: { ...assetModal.data, depreciation_method: v } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="straight_line">Straight-line</SelectItem>
                    <SelectItem value="declining_balance">Declining balance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Warranty Expiry</Label><Input type="date" value={assetModal.data?.warranty_expiry || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, warranty_expiry: e.target.value } })} /></div>
              <div><Label>Vendor</Label><Input value={assetModal.data?.vendor || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, vendor: e.target.value } })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={assetModal.data?.notes || ''} onChange={e => setAssetModal({ ...assetModal, data: { ...assetModal.data, notes: e.target.value } })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssetModal({ open: false, data: null })}>Cancel</Button>
            <Button onClick={saveAsset} data-testid="save-asset">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ ASSIGN MODAL ═══ */}
      <Dialog open={assignModal.open} onOpenChange={o => !o && setAssignModal({ open: false, asset: null, employee_id: '', notes: '', condition_notes: '', photos: [] })}>
        <DialogContent className="max-w-md" data-testid="assign-modal">
          <DialogHeader>
            <DialogTitle>Assign {assignModal.asset?.asset_code}</DialogTitle>
            <DialogDescription>{assignModal.asset?.brand} {assignModal.asset?.model}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employee *</Label>
              <Select value={assignModal.employee_id} onValueChange={v => setAssignModal({ ...assignModal, employee_id: v })}>
                <SelectTrigger data-testid="assign-employee"><SelectValue placeholder="Pick active employee" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name} {e.employee_id ? `(${e.employee_id})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={assignModal.notes} onChange={e => setAssignModal({ ...assignModal, notes: e.target.value })} /></div>
            <div><Label>Condition notes at hand-over</Label><Textarea rows={2} placeholder="e.g. small scratch on back lid" value={assignModal.condition_notes} onChange={e => setAssignModal({ ...assignModal, condition_notes: e.target.value })} /></div>
            <div>
              <Label className="flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Photos from all angles (optional)</Label>
              <input type="file" accept="image/*" multiple onChange={e => setAssignModal({ ...assignModal, photos: Array.from(e.target.files || []) })} data-testid="assign-photos" />
              {assignModal.photos?.length > 0 && <p className="text-xs text-muted-foreground mt-1">{assignModal.photos.length} photo(s) attached</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModal({ open: false, asset: null, employee_id: '', notes: '', condition_notes: '', photos: [] })}>Cancel</Button>
            <Button onClick={submitAssign} data-testid="submit-assign">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ RETURN MODAL ═══ */}
      <Dialog open={returnModal.open} onOpenChange={o => !o && setReturnModal({ open: false, asset: null, condition: 'good', return_notes: '', damage_summary: '', photos: [] })}>
        <DialogContent className="max-w-md" data-testid="return-modal">
          <DialogHeader>
            <DialogTitle>Return {returnModal.asset?.asset_code}</DialogTitle>
            <DialogDescription>From {returnModal.asset?.assigned_to_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Condition</Label>
              <Select value={returnModal.condition} onValueChange={v => setReturnModal({ ...returnModal, condition: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair (wear &amp; tear)</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Return notes</Label><Textarea rows={2} value={returnModal.return_notes} onChange={e => setReturnModal({ ...returnModal, return_notes: e.target.value })} /></div>
            <div><Label>Damage summary (if any)</Label><Textarea rows={2} placeholder="Visible damage that wasn't there at hand-over" value={returnModal.damage_summary} onChange={e => setReturnModal({ ...returnModal, damage_summary: e.target.value })} data-testid="return-damage" /></div>
            <div>
              <Label className="flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Return photos (optional)</Label>
              <input type="file" accept="image/*" multiple onChange={e => setReturnModal({ ...returnModal, photos: Array.from(e.target.files || []) })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnModal({ open: false, asset: null, condition: 'good', return_notes: '', damage_summary: '', photos: [] })}>Cancel</Button>
            <Button onClick={submitReturn} data-testid="submit-return">Confirm Return</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ SERVICE MODAL ═══ */}
      <Dialog open={serviceModal.open} onOpenChange={o => !o && setServiceModal({ open: false, asset: null, type: 'breakdown', cause: 'normal_use', charge_to: 'company', description: '', estimated_cost: '' })}>
        <DialogContent className="max-w-md" data-testid="service-modal">
          <DialogHeader>
            <DialogTitle>Open service for {serviceModal.asset?.asset_code}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={serviceModal.type} onValueChange={v => setServiceModal({ ...serviceModal, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakdown">Breakdown</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="wear_tear">Wear &amp; tear</SelectItem>
                    <SelectItem value="personal_damage">Personal damage</SelectItem>
                    <SelectItem value="upgrade">Upgrade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cause</Label>
                <Select value={serviceModal.cause} onValueChange={v => setServiceModal({ ...serviceModal, cause: v, charge_to: v === 'employee_negligence' ? 'employee' : 'company' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal_use">Normal use</SelectItem>
                    <SelectItem value="accident">Accident</SelectItem>
                    <SelectItem value="employee_negligence">Employee negligence</SelectItem>
                    <SelectItem value="manufacturer_defect">Manufacturer defect</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Charge to</Label>
              <Select value={serviceModal.charge_to} onValueChange={v => setServiceModal({ ...serviceModal, charge_to: v })}>
                <SelectTrigger data-testid="service-charge-to"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company (normal asset service)</SelectItem>
                  <SelectItem value="employee">Employee (personal damage — HR will be notified)</SelectItem>
                </SelectContent>
              </Select>
              {serviceModal.charge_to === 'employee' && (
                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> HR will be notified to handle the deduction manually.</p>
              )}
            </div>
            <div><Label>Description *</Label><Textarea rows={3} value={serviceModal.description} onChange={e => setServiceModal({ ...serviceModal, description: e.target.value })} data-testid="service-desc" /></div>
            <div><Label>Estimated cost (AED)</Label><Input type="number" step="0.01" value={serviceModal.estimated_cost} onChange={e => setServiceModal({ ...serviceModal, estimated_cost: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setServiceModal({ open: false, asset: null, type: 'breakdown', cause: 'normal_use', charge_to: 'company', description: '', estimated_cost: '' })}>Cancel</Button>
            <Button onClick={submitService} data-testid="submit-service">Open Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ DETAIL / QR MODAL ═══ */}
      <Dialog open={detailModal.open} onOpenChange={o => !o && setDetailModal({ open: false, asset: null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="asset-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" /> {detailModal.asset?.asset_code}
            </DialogTitle>
            <DialogDescription>{detailModal.asset?.brand} {detailModal.asset?.model}</DialogDescription>
          </DialogHeader>
          {detailModal.asset && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="border rounded-lg p-4 flex items-center justify-center bg-white min-h-[192px]">
                  {qrLoading
                    ? <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
                    : qrBlobUrl
                      ? <img src={qrBlobUrl} alt="QR" className="w-48 h-48" style={{ imageRendering: 'pixelated' }} data-testid="asset-qr-img" />
                      : <span className="text-xs text-muted-foreground">QR unavailable</span>}
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={!qrBlobUrl}
                  onClick={() => qrBlobUrl && window.open(qrBlobUrl, '_blank')}
                  data-testid="asset-qr-print-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-1.5" /> Open / Print Tag
                </Button>
              </div>
              <div className="space-y-2 text-sm">
                <KV k="UID" v={detailModal.asset.uid} />
                <KV k="Category" v={detailModal.asset.category_name} />
                <KV k="Status" v={<Badge variant="outline" className={STATUS_COLORS[detailModal.asset.status]}>{detailModal.asset.status}</Badge>} />
                <KV k="Serial" v={detailModal.asset.serial_number} />
                <KV k="IP / MAC" v={`${detailModal.asset.ip_address || '—'} / ${detailModal.asset.mac_address || '—'}`} />
                <KV k="Purchase date" v={detailModal.asset.purchase_date} />
                <KV k="Purchase value" v={fmtCur(detailModal.asset.purchase_value, detailModal.asset.currency)} />
                <KV k="Current value" v={<span className="text-emerald-700 font-mono">{fmtCur(detailModal.asset.current_value, detailModal.asset.currency)}</span>} />
                <KV k="Depreciation" v={`${detailModal.asset.depreciation_pct}%/yr (${detailModal.asset.depreciation_method?.replace('_', ' ')})`} />
                <KV k="Years owned" v={detailModal.asset.years_owned} />
                <KV k="Warranty until" v={detailModal.asset.warranty_expiry || '—'} />
                <KV k="Assigned to" v={detailModal.asset.assigned_to_name || '—'} />
              </div>
            </div>
          )}
          {detailModal.asset?.assignments?.length > 0 && (
            <div className="mt-3">
              <h3 className="text-sm font-semibold mb-2">Assignment History</h3>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {detailModal.asset.assignments.map(a => (
                  <div key={a.id} className="text-xs border rounded p-2">
                    <p><strong>{a.employee_name}</strong> — assigned {a.assigned_at?.slice(0, 10)} by {a.assigned_by_name}</p>
                    {a.returned_at && <p className="text-muted-foreground">Returned {a.returned_at?.slice(0, 10)} — condition: {a.return_condition}{a.damage_summary && ` — damage: ${a.damage_summary}`}</p>}
                    {(a.pre_assignment_photos?.length > 0 || a.return_photos?.length > 0) && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {[...(a.pre_assignment_photos || []).map(p => ({ ...p, tag: 'pre' })),
                          ...(a.return_photos || []).map(p => ({ ...p, tag: 'ret' }))].map((p, i) => (
                          <a key={i} href={p.data_url} target="_blank" rel="noreferrer" className="block">
                            <img src={p.data_url} alt="" className="h-12 w-12 object-cover rounded border" title={p.tag === 'pre' ? 'pre-assignment' : 'on return'} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, accent = 'slate' }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-xl font-bold mt-0.5 truncate">{value}</p>
          </div>
          {Icon && <Icon className={`h-7 w-7 text-${accent}-500/40`} />}
        </div>
      </CardContent>
    </Card>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-2 border-b last:border-b-0 py-1">
      <span className="text-muted-foreground text-xs">{k}</span>
      <span className="text-xs text-right break-all">{v ?? '—'}</span>
    </div>
  );
}
